import { Injectable } from '@angular/core';
import { DIALOG_CONTAINER_SELECTOR, hasVisibleDialogOverlay } from '../utils/dialog-keyboard-keys';

/**
 * Non-master-form Enter navigation (e.g. simple screens without appMasterFormKeyNav).
 * Master forms must not use this — they use MasterFormKeyNavDirective only.
 */
@Injectable({ providedIn: 'root' })
export class KeyboardNavigationService {
  private excludedSelectors = [
    '.sales-items-grid',
    'app-sales-billing',
    'app-sales-return-create',
    'app-purchase-return-create',
    'app-purchase-order',
    'app-party-grid',
    'textarea',
    '[contenteditable="true"]',
  ];

  /** Fields here handle their own Enter but stay valid Enter destinations from outside. */
  private ownEnterSelectors = ['.sa-grid-wrapper'];

  handleGlobalEnterKey(event: KeyboardEvent): boolean {
    const target = event.target as HTMLElement;

    if (target.closest('[appMasterFormKeyNav]')) {
      return false;
    }

    // Behind an open dialog, the page must not move focus.
    if (!target.closest(DIALOG_CONTAINER_SELECTOR) && hasVisibleDialogOverlay()) {
      return false;
    }

    if (!['INPUT', 'SELECT'].includes(target.tagName)) {
      return false;
    }

    if (target.tagName === 'TEXTAREA') {
      return false;
    }

    if (this.isInExcludedContainer(target) || this.ownEnterSelectors.some((selector) => target.closest(selector))) {
      return false;
    }

    // Open autocomplete/select keeps Enter so Material selects the highlighted option.
    if (this.isAutocompleteOpenForElement(target) || this.isSelectOpenForElement(target)) {
      return false;
    }

    if (this.hasCustomHandler(target)) {
      return false;
    }

    if (!this.focusNextField(target)) {
      return false;
    }
    event.preventDefault();
    return true;
  }

  private hasCustomHandler(element: HTMLElement): boolean {
    const hasKeydown =
      element.hasAttribute('(keydown)') ||
      element.hasAttribute('(keydown.enter)') ||
      element.getAttribute('ng-reflect-keydown') !== null;

    if (element.hasAttribute('data-skip-enter-navigation')) {
      return true;
    }

    const form = element.closest('form');
    if (form?.hasAttribute('data-skip-enter-navigation')) {
      return true;
    }

    return hasKeydown;
  }

  private isInExcludedContainer(element: HTMLElement): boolean {
    return this.excludedSelectors.some((selector) => {
      return element.closest(selector) !== null;
    });
  }

  /** Uses only the focused control + its aria-controls/aria-owns panel — no document-wide scan. */
  private isAutocompleteOpenForElement(element: HTMLElement): boolean {
    const inputElement = (element.closest('input[matAutocomplete]') ||
      (element.tagName === 'INPUT' ? element : null)) as HTMLInputElement | null;
    if (!inputElement) {
      return false;
    }
    if (inputElement.getAttribute('aria-expanded') !== 'true') {
      return false;
    }
    const panelId = inputElement.getAttribute('aria-controls') || inputElement.getAttribute('aria-owns');
    if (!panelId) {
      return false;
    }
    const panel = document.getElementById(panelId);
    if (!panel?.classList.contains('mat-mdc-autocomplete-panel') && !panel?.classList.contains('mat-autocomplete-panel')) {
      return false;
    }
    const style = window.getComputedStyle(panel);
    return style.display !== 'none' && style.visibility !== 'hidden' && (panel as HTMLElement).getClientRects().length > 0;
  }

  private isSelectOpenForElement(element: HTMLElement): boolean {
    const active = (element.closest('mat-select') || element) as HTMLElement | null;
    if (!active || active.tagName !== 'MAT-SELECT') {
      return false;
    }
    if (active.getAttribute('aria-expanded') !== 'true') {
      return false;
    }
    const panelId = active.getAttribute('aria-controls') || active.getAttribute('aria-owns');
    if (!panelId) {
      return false;
    }
    const panel = document.getElementById(panelId);
    if (!panel?.classList.contains('mat-mdc-select-panel') && !panel?.classList.contains('mat-select-panel')) {
      return false;
    }
    const style = window.getComputedStyle(panel);
    return style.display !== 'none' && style.visibility !== 'hidden' && panel.getClientRects().length > 0;
  }

  private focusNextField(currentElement: HTMLElement): boolean {
    const dialog = currentElement.closest<HTMLElement>(DIALOG_CONTAINER_SELECTOR);
    const formScope = currentElement.closest('form') || currentElement.closest('[class*="form"]');
    // Inside a dialog, Enter never leaves it.
    const form = dialog && !(formScope && dialog.contains(formScope)) ? dialog : formScope || document.body;

    const focusableSelector = `
      input:not([disabled]):not([readonly]):not([tabindex="-1"]):not([type="hidden"]):not([type="submit"]):not([type="button"]):not([type="reset"]),
      select:not([disabled]):not([readonly]):not([tabindex="-1"])
    `;

    const focusableElements = Array.from(form.querySelectorAll(focusableSelector)) as HTMLElement[];

    const validElements = focusableElements.filter(
      (el) => !this.isInExcludedContainer(el) && !this.hasCustomHandler(el) && this.isShown(el),
    );

    const currentIndex = validElements.indexOf(currentElement as HTMLElement);

    if (currentIndex === -1 || currentIndex >= validElements.length - 1) {
      return false;
    }
    const nextElement = validElements[currentIndex + 1];
    nextElement.focus();
    if (nextElement instanceof HTMLInputElement) {
      nextElement.select();
    }
    return true;
  }

  private isShown(element: HTMLElement): boolean {
    if (element.hidden || element.closest('[aria-hidden="true"]')) {
      return false;
    }
    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') {
      return false;
    }
    return element.getClientRects().length > 0;
  }
}
